import { offlineBasemapSource } from '@/config/basemaps'
import { martinSpriteUrl, martinTilesBaseUrl } from '@/config/env'
import { layers, namedFlavor } from '@protomaps/basemaps'
import type { StyleSpecification } from 'maplibre-gl'

const SOURCE_ID = 'protomaps'

/** Polices et icônes copiées dans public/basemap-offline (scripts/offline-basemap/fetch-offline-assets.mjs). */
function offlineAssetsBaseUrl(): string {
  const base = import.meta.env.BASE_URL.replace(/\/?$/, '/')
  return new URL(`${base}basemap-offline`, window.location.origin).href
}

/**
 * Les couches métier demandent d'autres polices (« Open Sans Bold », police par défaut
 * « Open Sans Regular »…) que seul le fournisseur en ligne sert. Hors ligne, ces glyphes
 * répondent 404 et MapLibre abandonne alors la tuile entière : la couche ne s'affiche pas.
 * On redirige donc toute police vers la Noto Sans locale de graisse la plus proche.
 */
function closestOfflineFont(fontstack: string): string {
  if (/bold|black|heavy|semibold|medium/i.test(fontstack)) return 'Noto Sans Medium'
  if (/italic|oblique/i.test(fontstack)) return 'Noto Sans Italic'
  return 'Noto Sans Regular'
}

/** transformRequest de la carte : réécrit les URL de glyphes du fond hors ligne. */
export function transformOfflineGlyphRequest(url: string, resourceType?: string): { url: string } | undefined {
  if (resourceType !== 'Glyphs') return undefined
  const fontsBase = `${offlineAssetsBaseUrl()}/fonts/`
  if (!url.startsWith(fontsBase)) return undefined
  const [fontstack, range] = url.slice(fontsBase.length).split('/')
  if (!fontstack || !range) return undefined
  const font = closestOfflineFont(decodeURIComponent(fontstack))
  return { url: `${fontsBase}${encodeURIComponent(font)}/${range}` }
}

/**
 * Style du fond hors ligne : tuiles vectorielles Protomaps du Cameroun servies par Martin,
 * polices et icônes servies par le front. Rien ne sort du réseau local.
 */
export function buildOfflineBasemapStyle(lang: string = 'fr'): StyleSpecification {
  const assets = offlineAssetsBaseUrl()
  const sprite: StyleSpecification['sprite'] = [{ id: 'default', url: `${assets}/sprites/light` }]
  // Mêmes icônes Maki qu'en ligne pour les couches OSM (préfixe « maki: »).
  if (martinSpriteUrl) sprite.push({ id: 'maki', url: martinSpriteUrl })

  return {
    version: 8,
    glyphs: `${assets}/fonts/{fontstack}/{range}.pbf`,
    sprite,
    sources: {
      [SOURCE_ID]: {
        type: 'vector',
        // TileJSON fourni par Martin ; une source à `url` est reconnue comme couche de fond par
        // MapService.updateStyle et remplacée au changement de fond.
        url: `${martinTilesBaseUrl}/${offlineBasemapSource}`,
        attribution:
          '<a href="https://protomaps.com">Protomaps</a> © <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    layers: layers(SOURCE_ID, namedFlavor('light'), { lang }),
  }
}
